// The office's notifications (QA-097), as the office-notifications prototype groups them: urgent
// unread ones under "Needs you now", then today's, then earlier ones, newest first in each. Which
// ones a person gets is the server's (notifications.list, by permission); this only orders them
// and says where each one opens.

import { dateText, timeText } from '@flowology/kernel';
import type { AgentsPart } from '../agents/props.ts';
import type { BooksPart } from '../books/props.ts';

export interface Note {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  entityType: string;
  entityId: string;
  urgent: boolean;
  createdAt: string;
  read: boolean;
}

/** notifications.list's answer, a page at a time: the counts are the server's, of every notification. */
export interface NotesPage {
  notifications: Note[];
  unread: number;
  urgent: number;
  total: number;
  next: string | null;
}

/** How many notifications are read at a time; the rest come on "Load more" (list caps, BL-110). */
export const NOTES_PAGE = 50;

/**
 * notifications.list's input, read and unread: a page, or on a refresh as many as were shown (200, the
 * server's most, at the outside); the next page after `after`.
 */
export const notesInput = (shown = 0, after?: string): Record<string, unknown> => ({
  unreadOnly: false,
  limit: Math.min(200, Math.max(NOTES_PAGE, shown)),
  ...(after ? { after } : {}),
});

export interface NoteGroup {
  key: 'now' | 'today' | 'earlier';
  label: string;
  notes: Note[];
}

const dayOf = (iso: string | Date, timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(iso),
  );

export function groupNotes(notes: readonly Note[], now: Date, timeZone: string): NoteGroup[] {
  const today = dayOf(now, timeZone);
  const newest = (a: Note, b: Note) => b.createdAt.localeCompare(a.createdAt);
  const pressing = (n: Note) => n.urgent && !n.read;
  const groups: NoteGroup[] = [
    { key: 'now', label: 'Needs you now', notes: notes.filter(pressing) },
    {
      key: 'today',
      label: 'Earlier today',
      notes: notes.filter((n) => !pressing(n) && dayOf(n.createdAt, timeZone) === today),
    },
    {
      key: 'earlier',
      label: 'Earlier',
      notes: notes.filter((n) => !pressing(n) && dayOf(n.createdAt, timeZone) !== today),
    },
  ];
  for (const g of groups) g.notes.sort(newest);
  return groups.filter((g) => g.notes.length > 0);
}

/** "10:42 a.m." today, "Yest." yesterday, else "Oct 14": the item's time, in the company's zone. */
export function noteTime(iso: string, now: Date, timeZone: string): string {
  const day = dayOf(iso, timeZone);
  if (day === dayOf(now, timeZone)) return timeText(iso, timeZone);
  if (day === dayOf(new Date(now.getTime() - 86_400_000), timeZone)) return 'Yest.';
  return dateText(iso, timeZone, { year: false });
}

/** The places a notification opens (a subset of Nav's views; kept here so Nav can import this). */
export type NoteView =
  | 'books'
  | 'tobook'
  | 'agents'
  | 'settings'
  | 'customers'
  | 'company'
  | 'review'
  | 'memberships'
  | 'tasks'
  | 'board';

export interface NoteSource {
  view: NoteView;
  /** For a Books notification: the part of Books it opens on. */
  part?: BooksPart;
  /** For an Agents notification: the part of Agents it opens on. */
  agentsPart?: AgentsPart;
  /** For a membership's renewal: Memberships opens on Renewals, with this membership's row in view. */
  membershipsPart?: 'renewals';
  focus?: string;
  /** A job whose page opens over the place (a tech's message on it, BL-028; one ready for review). */
  job?: string;
}

/**
 * Where a notification opens: the place that deals with its subject. The caller checks the person
 * may open that place; a notification with nowhere to go is only marked read.
 */
export function sourceOf(n: Pick<Note, 'kind' | 'entityType'> & { entityId?: string }): NoteSource | null {
  switch (n.entityType) {
    case 'posting': // an invoice or payment that can't post: an account is missing in Books setup
      return { view: 'books', part: 'setup' };
    case 'payment': // a payment online, or a card payment recorded late
      return { view: 'books', part: 'receivables' };
    case 'estimate': // sold for later, signed online, disputed, or a cancel request
      return { view: 'tobook' };
    case 'approval': // an agent's approved request that failed to run
      return { view: 'agents' };
    case 'agent_run': // an agent's month near its budget (M10)
      return { view: 'agents' };
    case 'message': // a text or email that wasn't sent: usually texting isn't set up, which Flowology support turns on
      return { view: 'agents', agentsPart: 'support' };
    case 'appointment': // a customer asking to move or cancel their visit (F-08): To book's Asked to move
      return n.kind.startsWith('visit.') ? { view: 'tobook' } : null;
    case 'membership': // a renewal at risk, declined, with no card or a card expiring first (BL-009): Renewals
      return {
        view: 'memberships',
        membershipsPart: 'renewals',
        ...(n.entityId ? { focus: n.entityId } : {}),
      };
    case 'job': // ready for review (QA-344); a tech wrote on it (BL-028); or a low rating: the customer to call back
      // the job's own page, over Review or the board: its Review area, or its history with the message
      if (n.kind === 'job.ready_for_review')
        return { view: 'review', ...(n.entityId ? { job: n.entityId } : {}) };
      if (n.kind === 'job_message.from_tech')
        return { view: 'board', ...(n.entityId ? { job: n.entityId } : {}) };
      return { view: 'customers' };
    case 'task': // given to you (BL-004)
      return { view: 'tasks' };
    case 'staff': // your tasks due today (BL-004); a staff notice of another kind goes nowhere
      return n.kind === 'task.due' ? { view: 'tasks' } : null;
    default:
      return null;
  }
}
